// @ts-strict-ignore
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { makeStyles } from 'tss-react/mui'
import { keyframes } from 'tss-react'
import { useForceUpdate } from 'hooks/utility'

const WRAP_HEIGHT_THRESHOLD = 48

const useStyles = makeStyles()(theme => ({
  actions: {
    color: '#FFF',
    marginTop: theme.spacing(2),
    marginLeft: theme.spacing(3),
    marginRight: theme.spacing(3),
    animation: `${keyframes`
      from: { opacity: 0 }
      to: { opacity: 1 }
    `} 0.4s ease-out backwards`,
    animationDelay: '400ms',
    display: 'flex',
    flexWrap: 'wrap' as 'wrap',
    '& > *': {
      marginRight: theme.spacing(2),
    },
    '& *:disabled': {
      opacity: '0.5 !important',
    },
    [theme.breakpoints.down('sm')]: {
      marginTop: theme.spacing(1),
    },
  },
  wrap: {
    justifyContent: 'space-evenly',
  },
  actionsNotAlternative: {
    '& *': {
      color: `${'#FFF'} !important`,
    },
  },
}))

interface IPageHeaderActionsProps extends React.HTMLAttributes<HTMLDivElement> {
  alternative?: boolean
  classes?: Partial<Record<string, string>>
}

export default function PageHeaderActions(props: IPageHeaderActionsProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const [wrap, setWrap] = useState<boolean>(false)
  const { classes } = useStyles()
  const { alternative, children, ...restProps } = props

  const forceUpdate = useForceUpdate()
  const handleWindowResize = useCallback(() => forceUpdate(), [forceUpdate])

  const getStyleName = () => {
    const styleNameList = [classes.actions]

    if (!alternative) styleNameList.push(classes.actionsNotAlternative)
    if (wrap) styleNameList.push(classes.wrap)

    return styleNameList.join(' ')
  }

  const actualizeWrapState = useCallback(() => {
    if (containerRef.current.getBoundingClientRect().height > WRAP_HEIGHT_THRESHOLD) {
      if (!wrap) {
        setWrap(true)
      }
    } else if (wrap) {
      setWrap(false)
    }
  }, [wrap])

  useEffect(() => {
    window.addEventListener('resize', handleWindowResize)
    actualizeWrapState()

    return () => {
      window.removeEventListener('resize', handleWindowResize)
    }
  }, [actualizeWrapState, handleWindowResize])

  return (
    <div ref={containerRef} className={getStyleName()} {...restProps}>
      {children}
    </div>
  )
}
